import { useState, useCallback, useEffect } from 'react'
import { message, PaginationProps } from 'antd'

export type Data = { total: number; list: any[]; totalCount?: number }

export type Service = (...args: any) => Promise<Data>

interface Props {
  initialParams: Record<string, any>
  service: Service
}
export const useTableData = ({ initialParams = {}, service }: Props) => {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState<PaginationProps>({
    current: 1,
    // pageSize: 10,
    pageSize: 20,
    total: 0,
    showTotal: total => `共${total}项数据`,
    showQuickJumper: true,
    showSizeChanger: true,
    // pageSizeOptions: [10, 20],
    pageSizeOptions: [10, 20, 50, 100],
    size: 'small',
  })
  const [params, setParams] = useState(initialParams)

  const loadData = async () => {
    setLoading(true)
    try {
      const result = await service({
        ...params,
        pageIndex: pagination.current,
        pageSize: pagination.pageSize,
      })
      setData(result.list)
      setPagination(prev => ({ ...prev, total: result.total || result.totalCount }))
    } catch (error) {
      message.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [params, pagination.current, pagination.pageSize])

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination)
  }

  // 添加一个 refresh 函数，支持控制是否重置页码，默认重置到第一页
  const refresh = (options: { resetPage?: boolean } = {}) => {
    const { resetPage = true } = options
    if (resetPage) {
      setPagination(prev => ({ ...prev, current: 1 }))
      // 当重置页码时，useEffect会自动触发loadData
      // 触发第一页数据刷新
      setParams(prev => ({...prev}))
    } else {
      loadData()
    }
  }

  // 搜索函数，搜索时，页码应该重置为 1
  const search = (params: Record<string, any> = {}) => {
    // 创建新对象，避免传入相同引用时 React 认为状态未变化而不触发请求
    setParams(() => ({ ...params }))
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  return {
    data,
    loading,
    pagination,
    refresh,
    handleTableChange,
    search,
    setParams, // 允许外部更新查询参数
  }
}
